// ============================================================================
// Property context: shared state for unified property detail (owner + co-host)
// ============================================================================

import {
  createContext,
  useContext,
  useMemo,
  ReactNode,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAccessibleProperties } from "@/hooks/useAccessibleProperties";
import type { PropertyUserRole } from "@/lib/propertyTabs";
import type { PropertyAccessLevel } from "@/lib/propertyTabs";
import {
  resolveTeamPermissions,
  type TeamPermissionModule,
} from "@/lib/teamPermissions";

export interface PropertyContextValue {
  propertyId: string | null;
  role: PropertyUserRole | null;
  accessLevel: PropertyAccessLevel | null;
  /** Slice 22: modules this cohost may fully use. Owners get all. */
  permissions: TeamPermissionModule[] | null;
  isOwner: boolean;
  accessibleProperties: { id: string; name: string; address?: string | null; city?: string | null }[];
  isLoading: boolean;
}

const PropertyContext = createContext<PropertyContextValue | null>(null);

export function usePropertyContext(): PropertyContextValue | null {
  return useContext(PropertyContext);
}

interface PropertyContextProviderProps {
  propertyId: string | undefined;
  children: ReactNode;
}

export function PropertyContextProvider({ propertyId, children }: PropertyContextProviderProps) {
  const { user, profile } = useAuth();
  const { is_cohost } = useUserCapabilities();
  const { properties: accessibleProperties, isLoading: accessibleLoading } = useAccessibleProperties();

  const { data: accessRow, isLoading: accessLoading } = useQuery({
    queryKey: ["my-property-access-level", propertyId, user?.id],
    queryFn: async () => {
      if (!propertyId || !user?.id) return null;
      const { data, error } = await supabase.rpc("get_my_property_access_level", {
        p_property_id: propertyId,
      });
      if (error) return null;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return null;
      return row as { access_level: string; is_owner: boolean; permissions?: unknown };
    },
    enabled: !!propertyId && !!user?.id && (profile?.role !== "cleaner" || is_cohost),
  });

  const value = useMemo((): PropertyContextValue => {
    const isOwner = accessRow?.is_owner === true;
    const role: PropertyUserRole | null = isOwner
      ? "owner"
      : accessRow?.access_level
        ? "cohost"
        : null;
    const accessLevel: PropertyAccessLevel | null =
      accessRow?.access_level === "full" || accessRow?.access_level === "limited"
        ? accessRow.access_level
        : isOwner
          ? "full"
          : null;

    const permissions: TeamPermissionModule[] | null = isOwner
      ? null
      : role === "cohost"
        ? resolveTeamPermissions(accessRow?.permissions ?? null, accessLevel)
        : null;

    return {
      propertyId: propertyId ?? null,
      role,
      accessLevel,
      permissions,
      isOwner,
      accessibleProperties: accessibleProperties.map((p) => ({
        id: p.id,
        name: p.name,
        address: p.address ?? null,
        city: p.city ?? null,
      })),
      isLoading: accessLoading || accessibleLoading,
    };
  }, [propertyId, accessRow, accessLoading, accessibleProperties, accessibleLoading]);

  return (
    <PropertyContext.Provider value={value}>
      {children}
    </PropertyContext.Provider>
  );
}
